import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Radio, Users, Mic, MicOff, Hand, LogOut, Plus, X, Loader2, Volume2, Crown, ShieldOff, ArrowLeft, MessageCircle, Calendar, CheckCircle2, Clock, Trash2, Play, UserPlus, Lock } from "lucide-react";
import { Room, RoomEvent, Track } from "livekit-client";
import { motion, AnimatePresence } from "framer-motion";
import UserPickerSheet from "../components/UserPickerSheet";

/* -------------------------- Countdown helper -------------------------- */
function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!iso) return { label: "", ms: 0 };
  const target = new Date(iso).getTime();
  const ms = target - now;
  if (ms <= 0) return { label: "Starting now", ms };
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return { label: `${d}d ${h}h ${m}m`, ms };
  if (h > 0) return { label: `${h}h ${m}m ${sec}s`, ms };
  if (m > 0) return { label: `${m}m ${sec}s`, ms };
  return { label: `${sec}s`, ms };
}

function fmtScheduledAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

/* -------------------------- Scheduled Room Tile -------------------------- */
function ScheduledTile({ room, onRSVPToggle, onStart, onCancel, onOpen }) {
  const cd = useCountdown(room.scheduled_at);
  const starting = cd.ms <= 0;
  return (
    <div data-testid={`scheduled-tile-${room.id}`}
      className="p-4 rounded-2xl bg-gradient-to-br from-[#FF7A00]/10 via-white/4 to-[#A855F7]/10 border border-[#FF7A00]/25">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF7A00]/20 border border-[#FF7A00]/45 text-[#FF7A00] text-[10px] font-black uppercase tracking-wider">
          <Calendar className="w-3 h-3"/>Room of the Hour
        </span>
        {starting && <span className="px-2 py-0.5 rounded-full bg-[#FF2A5F]/25 border border-[#FF2A5F]/45 text-[#FF2A5F] text-[10px] font-black uppercase animate-pulse">Ready</span>}
      </div>
      <div className="font-outfit font-bold text-base leading-tight">{room.title}</div>
      {room.description && <div className="text-xs text-white/65 mt-1 line-clamp-2">{room.description}</div>}
      <div className="mt-2 flex items-center gap-3 flex-wrap text-xs">
        <div className="flex items-center gap-1 text-white/75"><Clock className="w-3 h-3 text-[#FF7A00]"/><span data-testid={`scheduled-countdown-${room.id}`}>{cd.label}</span></div>
        <div className="text-white/50">· {fmtScheduledAt(room.scheduled_at)}</div>
        <div className="flex items-center gap-1 text-white/70"><Users className="w-3 h-3"/><span data-testid={`scheduled-rsvp-count-${room.id}`}>{room.rsvp_count || 0}</span> going</div>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-white/45">
        <span>Host</span>
        {room.host_avatar && <img src={room.host_avatar} alt="" className="w-4 h-4 rounded-full inline-block"/>}
        <span>@{room.host_username}</span>
      </div>
      <div className="mt-3 flex items-center gap-2">
        {room.is_host ? (
          <>
            <button onClick={() => onStart(room.id)} data-testid={`scheduled-start-${room.id}`}
              className="flex-1 px-3 py-2 rounded-full bg-gradient-to-r from-[#FF2A5F] to-[#A855F7] text-white font-outfit font-extrabold text-xs flex items-center justify-center gap-1 active:scale-95">
              <Play className="w-3.5 h-3.5"/>{starting ? "Go Live Now" : "Start Early"}
            </button>
            <button onClick={() => onCancel(room.id)} data-testid={`scheduled-cancel-${room.id}`}
              className="p-2 rounded-full bg-white/8 border border-white/10 text-white/70 active:scale-95" title="Cancel">
              <Trash2 className="w-3.5 h-3.5"/>
            </button>
          </>
        ) : (
          <button onClick={() => onRSVPToggle(room.id)} data-testid={`scheduled-rsvp-${room.id}`}
            className={`flex-1 px-3 py-2 rounded-full font-outfit font-extrabold text-xs flex items-center justify-center gap-1 active:scale-95 ${room.i_rsvpd ? "bg-emerald-500/25 border border-emerald-400/50 text-emerald-300" : "bg-white/8 border border-white/15 text-white/85"}`}>
            {room.i_rsvpd ? <><CheckCircle2 className="w-3.5 h-3.5"/>Going ✓</> : <><Calendar className="w-3.5 h-3.5"/>RSVP</>}
          </button>
        )}
      </div>
    </div>
  );
}

/* -------------------------- Room List -------------------------- */
function RoomList({ onOpen, onCreate, onSchedule, canSchedule, onLiveFromScheduled, reloadKey }) {
  const [rooms, setRooms] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/rooms").then(r => r.data || []).catch(() => []),
      api.get("/rooms/scheduled").then(r => r.data || []).catch(() => []),
      api.get("/rooms/my-invites").then(r => r.data || []).catch(() => []),
    ]).then(([live, sched, inv]) => { setRooms(live); setScheduled(sched); setInvites(inv); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [reloadKey]);

  const rsvpToggle = async (id) => {
    try {
      const { data } = await api.post(`/rooms/${id}/rsvp`);
      setScheduled(prev => prev.map(r => r.id === id ? { ...r, i_rsvpd: data.i_rsvpd, rsvp_count: data.rsvp_count } : r));
    } catch (e) { window.alert(formatErr(e)); }
  };
  const startNow = async (id) => {
    try {
      const { data } = await api.post(`/rooms/${id}/start`);
      onLiveFromScheduled(data);
    } catch (e) { window.alert(formatErr(e)); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancel this scheduled room? RSVPs will be notified.")) return;
    try { await api.delete(`/rooms/${id}/schedule`); setScheduled(prev => prev.filter(r => r.id !== id)); }
    catch (e) { window.alert(formatErr(e)); }
  };

  return (
    <div className="px-4 py-5 pb-24" data-testid="rooms-list">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Voice Chat Room</h1>
          <p className="text-white/60 text-sm">Live audio conversations</p>
        </div>
        <div className="flex items-center gap-2">
          {canSchedule && (
            <button onClick={onSchedule} data-testid="rooms-schedule-btn"
              className="px-3 py-2.5 rounded-full bg-[#FF7A00]/15 border border-[#FF7A00]/40 text-[#FF7A00] font-outfit font-extrabold text-xs flex items-center gap-1 active:scale-95">
              <Calendar className="w-3.5 h-3.5"/>Schedule
            </button>
          )}
          <button onClick={onCreate} data-testid="rooms-create-btn"
            className="px-4 py-2.5 rounded-full bg-gradient-to-r from-[#FF2A5F] to-[#A855F7] text-white font-outfit font-extrabold flex items-center gap-1.5 active:scale-95">
            <Plus className="w-4 h-4"/>Create
          </button>
        </div>
      </div>

      {scheduled.length > 0 && (
        <div className="mb-6" data-testid="scheduled-section">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[10px] uppercase tracking-widest text-[#FF7A00] font-black flex items-center gap-1"><Calendar className="w-3 h-3"/>Room of the Hour · Upcoming</div>
          </div>
          <div className="space-y-2.5">
            {scheduled.map(r => (
              <ScheduledTile key={r.id} room={r} onRSVPToggle={rsvpToggle} onStart={startNow} onCancel={cancel} onOpen={onOpen}/>
            ))}
          </div>
        </div>
      )}

      <div className="p-3 rounded-2xl bg-[#A855F7]/8 border border-[#A855F7]/25 text-[11px] text-white/70 mb-4 flex items-start gap-2">
        <Lock className="w-3.5 h-3.5 text-[#A855F7] flex-shrink-0 mt-0.5"/>
        <span>All voice rooms are <b className="text-[#A855F7]">invite-only</b>. Only the host can invite users. Room capacity: up to 20 participants.</span>
      </div>

      {invites.length > 0 && (
        <div className="mb-5" data-testid="invites-section">
          <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-black mb-2 flex items-center gap-1"><UserPlus className="w-3 h-3"/>You&apos;re invited</div>
          <div className="space-y-2.5">
            {invites.map(r => (
              <button key={r.id} onClick={() => onOpen(r.id)} data-testid={`invite-tile-${r.id}`}
                className="w-full text-left p-4 rounded-2xl bg-gradient-to-br from-[#00E5FF]/12 to-[#A855F7]/8 border border-[#00E5FF]/35 active:scale-[0.99]">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#00E5FF]/20 border border-[#00E5FF]/40 text-[#00E5FF] text-[10px] font-black uppercase">
                    <UserPlus className="w-3 h-3"/>Invited
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF2A5F]/20 border border-[#FF2A5F]/40 text-[#FF2A5F] text-[10px] font-black uppercase">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF2A5F] animate-pulse"/>Live
                  </span>
                </div>
                <div className="font-outfit font-bold text-base leading-tight">{r.title}</div>
                {r.topic && <div className="text-xs text-white/60 mt-0.5 line-clamp-1">{r.topic}</div>}
                <div className="mt-2 flex items-center gap-3 text-xs text-white/70">
                  <div className="flex items-center gap-1"><Users className="w-3 h-3"/>{r.participant_count || 0}/{r.max_participants || 20}</div>
                  <div className="text-[10px] text-white/50">Host @{r.host_username}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2 flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF2A5F] animate-pulse"/>Your Rooms
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]"/></div>
      ) : rooms.length === 0 ? (
        <div className="text-center py-12 text-white/50 text-sm">You&apos;re not in any active voice rooms right now. Tap <b className="text-white/80">Create</b> to start one.</div>
      ) : (
        <div className="space-y-2.5" data-testid="rooms-grid">
          {rooms.map(r => (
            <button key={r.id} onClick={() => onOpen(r.id)} data-testid={`room-tile-${r.id}`}
              className="w-full text-left p-4 rounded-2xl bg-gradient-to-br from-white/6 to-white/2 border border-white/10 active:scale-[0.99] hover:border-[#00E5FF]/40">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF2A5F]/20 border border-[#FF2A5F]/40 text-[#FF2A5F] text-[10px] font-black uppercase">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF2A5F] animate-pulse"/>Live
                </span>
                {r.mocked && <span className="text-[9px] text-[#FF7A00] font-bold">MOCK</span>}
              </div>
              <div className="font-outfit font-bold text-base leading-tight">{r.title}</div>
              {r.topic && <div className="text-xs text-white/60 mt-0.5 line-clamp-1">{r.topic}</div>}
              <div className="mt-2 flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1 text-white/70"><Mic className="w-3 h-3"/>{(r.speakers || []).length} speaker{(r.speakers || []).length !== 1 ? "s" : ""}</div>
                <div className="flex items-center gap-1 text-white/70"><Volume2 className="w-3 h-3"/>{r.listener_count || 0} listener{r.listener_count !== 1 ? "s" : ""}</div>
                {r.raised_hands > 0 && <div className="flex items-center gap-1 text-[#FF7A00] font-bold"><Hand className="w-3 h-3"/>{r.raised_hands}</div>}
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-white/45">
                <span>Host</span>
                {r.host_avatar && <img src={r.host_avatar} alt="" className="w-4 h-4 rounded-full inline-block"/>}
                <span>@{r.host_username}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------- Create Room Modal -------------------------- */
function CreateRoomModal({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (!open) { setTitle(""); setTopic(""); setErr(""); } }, [open]);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!title.trim()) { setErr("Enter a title"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/rooms", { title: title.trim(), topic: topic.trim() });
      onCreated(data);
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };
  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center">
        <div className="absolute inset-0 bg-black/70" onClick={onClose}/>
        <motion.form onSubmit={submit} initial={{y:"100%"}} animate={{y:0}} exit={{y:"100%"}} transition={{type:"spring", damping:26}}
          data-testid="rooms-create-modal"
          className="relative w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl bg-[#0B0F19] border border-white/15 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-outfit font-extrabold text-lg">Start a Voice Chat Room</h3>
            <button type="button" onClick={onClose} data-testid="rooms-create-close" className="p-1.5 rounded-full bg-white/8"><X className="w-4 h-4"/></button>
          </div>
          <div className="p-2.5 rounded-2xl bg-[#A855F7]/8 border border-[#A855F7]/25 text-[11px] text-white/75 flex items-start gap-2">
            <Lock className="w-3.5 h-3.5 text-[#A855F7] flex-shrink-0 mt-0.5"/>
            <span>Every voice room is <b className="text-[#A855F7]">invite-only</b>. After you go live, tap <b>Invite</b> to bring in up to 20 users.</span>
          </div>
          <input value={title} onChange={e => setTitle(e.target.value)} required maxLength={120}
            placeholder="What's this room about?" data-testid="rooms-title"
            className="w-full px-3 py-3 rounded-2xl bg-white/5 border border-white/15 outline-none focus:border-[#00E5FF]"/>
          <textarea rows={2} value={topic} onChange={e => setTopic(e.target.value)} maxLength={180}
            placeholder="Optional short description…" data-testid="rooms-topic"
            className="w-full px-3 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none placeholder:text-white/40"/>
          {err && <div className="text-[#FF2A5F] text-xs" data-testid="rooms-create-err">{err}</div>}
          <button disabled={busy} data-testid="rooms-create-submit"
            className="w-full py-3 rounded-full bg-gradient-to-r from-[#FF2A5F] to-[#A855F7] text-white font-outfit font-extrabold flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-95">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Radio className="w-4 h-4"/>}
            Go Live (Invite-only)
          </button>
        </motion.form>
      </motion.div>
    </AnimatePresence>
  );
}

/* -------------------------- Schedule Room Modal -------------------------- */
function ScheduleRoomModal({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("");
  const [description, setDescription] = useState("");
  // Default: 1 hour from now, minute-rounded up
  const defaultLocal = () => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [when, setWhen] = useState(defaultLocal());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (!open) { setTitle(""); setTopic(""); setDescription(""); setWhen(defaultLocal()); setErr(""); } }, [open]);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!title.trim()) { setErr("Enter a title"); return; }
    if (!when) { setErr("Pick a date and time"); return; }
    // Convert local datetime-local (no TZ) → ISO with local offset → UTC
    const dt = new Date(when);
    if (isNaN(dt.getTime())) { setErr("Invalid date"); return; }
    if (dt.getTime() < Date.now() - 60_000) { setErr("Time must be in the future"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/rooms/schedule", {
        title: title.trim(),
        topic: topic.trim(),
        description: description.trim(),
        scheduled_at: dt.toISOString(),
      });
      onCreated(data);
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };
  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center">
        <div className="absolute inset-0 bg-black/70" onClick={onClose}/>
        <motion.form onSubmit={submit} initial={{y:"100%"}} animate={{y:0}} exit={{y:"100%"}} transition={{type:"spring", damping:26}}
          data-testid="rooms-schedule-modal"
          className="relative w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl bg-[#0B0F19] border border-white/15 p-4 space-y-3 max-h-[90dvh] overflow-y-auto">
          <div className="flex items-center justify-between sticky top-0 bg-[#0B0F19] pb-1 -mx-4 px-4 pt-1 border-b border-white/5">
            <h3 className="font-outfit font-extrabold text-lg flex items-center gap-1.5"><Calendar className="w-5 h-5 text-[#FF7A00]"/>Schedule Room</h3>
            <button type="button" onClick={onClose} data-testid="rooms-schedule-close" className="p-1.5 rounded-full bg-white/8"><X className="w-4 h-4"/></button>
          </div>
          <p className="text-[11px] text-white/55">Announce a &quot;Room of the Hour&quot; — listeners can RSVP and get an in-app notification when you go live.</p>
          <input value={title} onChange={e => setTitle(e.target.value)} required maxLength={120}
            placeholder="Room title (e.g. Chai & Web3 with @you)" data-testid="rooms-schedule-title"
            className="w-full px-3 py-3 rounded-2xl bg-white/5 border border-white/15 outline-none focus:border-[#FF7A00]"/>
          <input value={topic} onChange={e => setTopic(e.target.value)} maxLength={180}
            placeholder="Short topic (optional)" data-testid="rooms-schedule-topic"
            className="w-full px-3 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none placeholder:text-white/40"/>
          <textarea rows={3} value={description} onChange={e => setDescription(e.target.value)} maxLength={500}
            placeholder="What will you discuss? (optional, up to 500 chars)" data-testid="rooms-schedule-description"
            className="w-full px-3 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none placeholder:text-white/40"/>
          <label className="block">
            <span className="text-[11px] text-white/60 uppercase tracking-wider font-bold">Start time</span>
            <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} required
              data-testid="rooms-schedule-when"
              className="mt-1 w-full px-3 py-3 rounded-2xl bg-white/5 border border-white/15 outline-none focus:border-[#FF7A00] [color-scheme:dark]"/>
          </label>
          {err && <div className="text-[#FF2A5F] text-xs" data-testid="rooms-schedule-err">{err}</div>}
          <button disabled={busy} data-testid="rooms-schedule-submit"
            className="w-full py-3 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-outfit font-extrabold flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-95">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Calendar className="w-4 h-4"/>}
            Schedule Room
          </button>
        </motion.form>
      </motion.div>
    </AnimatePresence>
  );
}

/* -------------------------- Live Room -------------------------- */
function LiveRoom({ room: initial, onLeave }) {
  const { user } = useAuth();
  const [room, setRoom] = useState(initial);
  const [myRole, setMyRole] = useState(initial.my_role || "listener");
  const [micOn, setMicOn] = useState(myRole !== "listener");
  const [audioMuted, setAudioMuted] = useState(false); // start unmuted; browser will error to true if it blocks autoplay
  const [handRaised, setHandRaised] = useState(false);
  const [status, setStatus] = useState("connecting");
  const [err, setErr] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const roomRef = useRef(null);
  const audioBucketRef = useRef(null);
  const isHost = myRole === "host";
  const canSpeak = myRole === "host" || myRole === "speaker";

  const refreshSnapshot = async () => {
    try { const r = await api.get(`/rooms/${room.id}`); setRoom(prev => ({ ...prev, ...r.data })); } catch { /* noop */ }
  };

  // Connect LiveKit
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!room.livekit_url || !room.livekit_token) { setStatus("mocked"); return; }
      try {
        const lkRoom = new Room({ adaptiveStream: true, dynacast: true });
        lkRoom.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === Track.Kind.Audio && audioBucketRef.current) {
            const el = track.attach();
            el.autoplay = true; el.muted = audioMuted;
            audioBucketRef.current.appendChild(el);
            // Try to auto-unmute; if browser blocks, fall back to Tap-to-hear pill.
            el.play().catch(() => { el.muted = true; setAudioMuted(true); });
          }
        });
        lkRoom.on(RoomEvent.Disconnected, () => setStatus("disconnected"));
        await lkRoom.connect(room.livekit_url, room.livekit_token);
        if (cancelled) { await lkRoom.disconnect(); return; }
        roomRef.current = lkRoom;
        setStatus("connected");
        if (canSpeak) await lkRoom.localParticipant.setMicrophoneEnabled(true);
      } catch (e) { setErr("Voice connection failed: " + (e?.message || e)); setStatus("error"); }
    })();
    return () => {
      cancelled = true;
      if (roomRef.current) { try { roomRef.current.disconnect(); } catch { /* noop */ } roomRef.current = null; }
    };
  }, []);

  // Poll room snapshot every 5s
  useEffect(() => { const t = setInterval(refreshSnapshot, 5000); return () => clearInterval(t); }, [room.id]);

  // Dedicated WS to receive role-change / room-ended events for this room
  useEffect(() => {
    const token = localStorage.getItem("hf_token");
    if (!token) return;
    const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = API_BASE.replace(/^http/, "ws") + `/api/ws/dm?token=${token}`;
    let ws;
    try { ws = new WebSocket(wsUrl); } catch { return; }
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        window.dispatchEvent(new CustomEvent("hf_ws", { detail: msg }));
      } catch { /* noop */ }
    };
    return () => { try { ws.close(); } catch { /* noop */ } };
  }, [room.id]);

  // Listen for role changes & room end broadcasts via useAuth().ws or window event
  useEffect(() => {
    const handler = async (e) => {
      const msg = e.detail;
      if (!msg || msg?.data?.room_id !== room.id) return;
      if (msg.type === "room_promoted" || msg.type === "room_demoted") {
        const newRole = msg.data.my_role;
        const newToken = msg.data.livekit_token;
        setMyRole(newRole);
        // Re-connect LiveKit with the fresh publisher/subscriber token
        if (roomRef.current) { try { await roomRef.current.disconnect(); } catch { /* noop */ } }
        if (newToken && room.livekit_url) {
          try {
            const lkRoom = new Room({ adaptiveStream: true, dynacast: true });
            lkRoom.on(RoomEvent.TrackSubscribed, (track) => {
              if (track.kind === Track.Kind.Audio && audioBucketRef.current) {
                const el = track.attach(); el.autoplay = true; el.muted = audioMuted;
                audioBucketRef.current.appendChild(el);
                el.play().catch(() => { el.muted = true; setAudioMuted(true); });
              }
            });
            await lkRoom.connect(room.livekit_url, newToken);
            roomRef.current = lkRoom;
            if (newRole !== "listener") await lkRoom.localParticipant.setMicrophoneEnabled(true);
            setMicOn(newRole !== "listener");
          } catch { /* noop */ }
        }
      } else if (msg.type === "room_ended" || msg.type === "room_kicked") {
        onLeave();
      } else if (msg.type === "room_muted") {
        setMicOn(false);
        try { if (roomRef.current) await roomRef.current.localParticipant.setMicrophoneEnabled(false); } catch { /* noop */ }
      }
    };
    window.addEventListener("hf_ws", handler);
    return () => window.removeEventListener("hf_ws", handler);
  }, [room.id, room.livekit_url, audioMuted, onLeave]);

  const unmuteAudio = async () => {
    setAudioMuted(false);
    if (audioBucketRef.current) {
      Array.from(audioBucketRef.current.children).forEach(el => { try { el.muted = false; el.play(); } catch { /* noop */ } });
    }
  };

  const toggleMic = async () => {
    if (!canSpeak) return;
    const next = !micOn;
    setMicOn(next);
    try { if (roomRef.current) await roomRef.current.localParticipant.setMicrophoneEnabled(next); } catch { /* noop */ }
  };

  const raiseHand = async () => {
    try { const { data } = await api.post(`/rooms/${room.id}/hand`); setHandRaised(data.hand_raised); }
    catch { /* noop */ }
  };

  const leave = async () => {
    try { await api.post(`/rooms/${room.id}/leave`); } catch { /* noop */ }
    onLeave();
  };
  const endRoom = async () => {
    if (!window.confirm("End this room for everyone?")) return;
    try { await api.post(`/rooms/${room.id}/end`); onLeave(); } catch (e) { setErr(formatErr(e)); }
  };

  const promote = async (uid) => { try { await api.post(`/rooms/${room.id}/promote`, { target_user_id: uid }); refreshSnapshot(); } catch { /* noop */ } };
  const demote  = async (uid) => { try { await api.post(`/rooms/${room.id}/demote`, { target_user_id: uid }); refreshSnapshot(); } catch { /* noop */ } };
  const muteOne = async (uid) => { try { await api.post(`/rooms/${room.id}/mute`, { target_user_id: uid }); refreshSnapshot(); } catch { /* noop */ } };
  const kick    = async (uid) => { if (!window.confirm("Remove this participant?")) return; try { await api.post(`/rooms/${room.id}/kick`, { target_user_id: uid }); refreshSnapshot(); } catch { /* noop */ } };

  const sendInvites = async (users) => {
    if (!users?.length) { setInviteOpen(false); return; }
    try {
      const { data } = await api.post(`/rooms/${room.id}/invite`, { user_ids: users.map(u => u.id) });
      setInviteOpen(false);
      window.alert(`Invited ${data.added?.length || 0} user${(data.added?.length || 0) === 1 ? "" : "s"} to this room. They'll get a notification.`);
      refreshSnapshot();
    } catch (e) { window.alert(formatErr(e)); }
  };

  const speakers = room.speakers || [];

  return (
    <div className="fixed inset-0 z-[55] bg-gradient-to-br from-[#12071F] via-[#1A0F2E] to-[#0B0F19] flex flex-col" data-testid="rooms-live-screen">
      <audio ref={audioBucketRef} style={{ display: "none" }}/>

      <div className="p-3 flex items-center justify-between border-b border-white/10">
        <button onClick={leave} data-testid="rooms-back" className="p-2 rounded-full bg-white/8"><ArrowLeft className="w-4 h-4"/></button>
        <div className="flex-1 mx-3 text-center min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#FF2A5F] font-black flex items-center justify-center gap-1"><Lock className="w-2.5 h-2.5"/>Voice Chat Room</div>
          <div className="text-sm font-outfit font-bold truncate">{room.title}</div>
        </div>
        <div className="flex items-center gap-2">
          {isHost && (
            <button onClick={() => setInviteOpen(true)} data-testid="rooms-invite-btn"
              className="px-2.5 py-1 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] text-[11px] font-black flex items-center gap-1 active:scale-95">
              <UserPlus className="w-3 h-3"/>Invite
            </button>
          )}
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/8 text-xs">
            <Users className="w-3 h-3"/>{(room.listener_count || 0) + speakers.length}/{room.max_participants || 20}
          </div>
        </div>
      </div>

      <UserPickerSheet open={inviteOpen} onClose={() => setInviteOpen(false)} kind="invite"
        title="Invite to Voice Room" onDone={sendInvites}/>

      {room.topic && <p className="px-4 pt-2 text-xs text-white/70">{room.topic}</p>}
      {status !== "connected" && (
        <div className={`mx-4 mt-3 px-3 py-1.5 rounded-full text-[11px] font-bold text-center ${status === "error" ? "bg-[#FF2A5F]/20 text-[#FF2A5F]" : "bg-[#FF7A00]/15 text-[#FF7A00]"}`} data-testid="rooms-status">
          {status === "connecting" && "🎙 Connecting to voice..."}
          {status === "mocked" && "🎧 Voice preview (LiveKit not configured yet)"}
          {status === "error" && err}
          {status === "disconnected" && "Voice disconnected"}
        </div>
      )}

      {/* Speakers grid */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-3">On Stage · {speakers.length}</div>
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="rooms-speakers">
          {speakers.map(s => (
            <div key={s.user_id} data-testid={`rooms-speaker-${s.user_id}`} className="flex flex-col items-center text-center">
              <div className="relative">
                <img src={s.avatar} alt=""
                  className={`w-16 h-16 rounded-full object-cover border-2 ${s.role === "host" ? "border-[#FF7A00] shadow-[0_0_20px_rgba(255,215,0,0.5)]" : "border-[#00E5FF]"}`}/>
                {s.role === "host" && <Crown className="w-4 h-4 absolute -top-1 -right-1 text-[#FF7A00] fill-[#FF7A00]"/>}
                {s.muted && <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#FF2A5F] flex items-center justify-center border-2 border-[#12071F]"><MicOff className="w-3 h-3"/></div>}
              </div>
              <div className="mt-1.5 text-xs font-bold truncate max-w-full">{s.name || s.username}</div>
              <div className="text-[9px] text-white/50">@{s.username}</div>
              {isHost && s.user_id !== user?.id && (
                <div className="mt-1.5 flex gap-1">
                  <button onClick={() => muteOne(s.user_id)} data-testid={`rooms-mute-${s.user_id}`} title="Mute" className="p-1 rounded-full bg-white/8 active:scale-90"><MicOff className="w-3 h-3"/></button>
                  <button onClick={() => demote(s.user_id)} data-testid={`rooms-demote-${s.user_id}`} title="Move to listener" className="p-1 rounded-full bg-white/8 active:scale-90"><ShieldOff className="w-3 h-3"/></button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Listeners (host view has kick + promote) */}
        {isHost && room.raised_hands > 0 && (
          <div className="p-3 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/30 mb-4">
            <div className="text-[10px] uppercase tracking-wider text-[#FF7A00] font-bold mb-1 flex items-center gap-1"><Hand className="w-3 h-3"/>{room.raised_hands} hand{room.raised_hands > 1 ? "s" : ""} raised</div>
            <p className="text-[11px] text-white/65">Tap 🎤 on a listener to promote them (auto-refreshes every 5s)</p>
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div className="p-4 border-t border-white/10 flex items-center justify-between bg-black/40 backdrop-blur">
        {audioMuted && !canSpeak && (
          <button onClick={unmuteAudio} data-testid="rooms-unmute-audio"
            className="mr-2 px-3 py-2 rounded-full bg-[#00E5FF] text-black font-bold text-xs flex items-center gap-1 animate-pulse active:scale-95">
            <Volume2 className="w-3.5 h-3.5"/>Tap to hear
          </button>
        )}
        {canSpeak ? (
          <button onClick={toggleMic} data-testid="rooms-toggle-mic"
            className={`px-4 py-2.5 rounded-full font-outfit font-extrabold flex items-center gap-1.5 active:scale-95 ${micOn ? "bg-emerald-500/25 border border-emerald-400/50 text-emerald-300" : "bg-[#FF2A5F]/25 border border-[#FF2A5F]/50 text-[#FF2A5F]"}`}>
            {micOn ? <Mic className="w-4 h-4"/> : <MicOff className="w-4 h-4"/>}
            {micOn ? "Mic On" : "Mic Off"}
          </button>
        ) : (
          <button onClick={raiseHand} data-testid="rooms-raise-hand"
            className={`px-4 py-2.5 rounded-full font-outfit font-extrabold flex items-center gap-1.5 active:scale-95 ${handRaised ? "bg-[#FF7A00] text-black" : "bg-white/8 border border-white/10 text-white/85"}`}>
            <Hand className="w-4 h-4"/>{handRaised ? "Hand up ✓" : "Raise hand"}
          </button>
        )}
        <div className="flex-1"/>
        {isHost ? (
          <button onClick={endRoom} data-testid="rooms-end"
            className="px-4 py-2.5 rounded-full bg-[#FF2A5F] text-white font-outfit font-extrabold flex items-center gap-1.5 active:scale-95">
            <LogOut className="w-4 h-4"/>End
          </button>
        ) : (
          <button onClick={leave} data-testid="rooms-leave"
            className="px-4 py-2.5 rounded-full bg-white/8 border border-white/15 text-white/85 font-outfit font-extrabold flex items-center gap-1.5 active:scale-95">
            <LogOut className="w-4 h-4"/>Leave
          </button>
        )}
      </div>
    </div>
  );
}

/* -------------------------- Default export -------------------------- */
export default function Rooms() {
  const [createOpen, setCreateOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [activeRoom, setActiveRoom] = useState(null);
  const [canSchedule, setCanSchedule] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    api.get("/rooms/eligibility").then(r => setCanSchedule(!!r.data?.can_schedule)).catch(() => setCanSchedule(false));
  }, []);

  const openRoom = async (id) => {
    try { const { data } = await api.post(`/rooms/${id}/join`); setActiveRoom(data); }
    catch (e) { window.alert(formatErr(e)); }
  };

  // Deep-link: /rooms?open=<room_id> (from notification when a scheduled room goes live)
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && !activeRoom) {
      openRoom(openId);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  // When any scheduled room the user RSVP'd to goes live, refetch immediately (don't wait 15s).
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail;
      if (msg?.type === "room_going_live" && !activeRoom) {
        setReloadKey(k => k + 1);
      }
    };
    window.addEventListener("hf_ws", handler);
    return () => window.removeEventListener("hf_ws", handler);
  }, [activeRoom]);

  if (activeRoom) return <LiveRoom room={activeRoom} onLeave={() => setActiveRoom(null)}/>;

  return (
    <>
      <RoomList
        onOpen={openRoom}
        onCreate={() => setCreateOpen(true)}
        onSchedule={() => setScheduleOpen(true)}
        canSchedule={canSchedule}
        onLiveFromScheduled={(data) => setActiveRoom(data)}
        reloadKey={reloadKey}
      />
      <CreateRoomModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(data) => { setCreateOpen(false); setActiveRoom(data); }}/>
      <ScheduleRoomModal open={scheduleOpen} onClose={() => setScheduleOpen(false)} onCreated={() => {
        setScheduleOpen(false);
        setReloadKey(k => k + 1);
      }}/>
    </>
  );
}
